import { useEffect, useState } from "react";
import { BarChart, Bar, LineChart, Line, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import {
  Loader2, Megaphone, ShieldCheck, Send, Plus, Search, X, Siren, CheckCircle2,
  Lock, EyeOff, User, Clock, AlertTriangle, TrendingUp, Smile, Meh, Frown,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader, StatCard, scoreColor } from "@/components/common/ui";
import { cn } from "@/lib/utils";

const TABS = ["Cases", "Analytics", "Surveys"];
const ANON = { anonymous: { icon: EyeOff, label: "Anonymous", c: "#7C3AED" }, confidential: { icon: Lock, label: "Confidential", c: "#F79009" }, identified: { icon: User, label: "Identified", c: "#155EEF" } };
const SENT = { Positive: { icon: Smile, c: "#12B76A" }, Neutral: { icon: Meh, c: "#155EEF" }, Negative: { icon: Frown, c: "#F04438" } };
const STATUS = { new: "#F79009", triage: "#7C3AED", "in-progress": "#155EEF", escalated: "#F04438", resolved: "#12B76A" };

export default function InsightBridge() {
  const [tab, setTab] = useState("Cases");
  const [cases, setCases] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [surveys, setSurveys] = useState([]);
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [submitOpen, setSubmitOpen] = useState(false);
  const [trackOpen, setTrackOpen] = useState(false);

  const loadCases = () => api.get("/insight/cases").then((r) => setCases(r.data)).catch(() => setCases([]));
  useEffect(() => {
    loadCases();
    api.get("/insight/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
    api.get("/insight/surveys").then((r) => setSurveys(r.data)).catch(() => {});
  }, []);

  const openCase = async (id) => {
    const { data } = await api.get(`/insight/cases/${id}`);
    setActive(data);
  };
  const sendReply = async () => {
    if (!reply.trim() || !active) return;
    const { data } = await api.post(`/insight/cases/${active.id}/messages`, { content: reply });
    setActive({ ...active, messages: [...(active.messages || []), data] }); setReply("");
  };
  const setStatus = async (status) => {
    await api.post(`/insight/cases/${active.id}/status`, { status });
    toast.success(`Case ${status}`); openCase(active.id); loadCases();
    if (status === "resolved") api.get("/insight/analytics").then((r) => setAnalytics(r.data));
  };

  if (!cases) return <div className="flex h-64 items-center justify-center text-muted-foreground"><Loader2 className="animate-spin" /></div>;

  return (
    <div>
      <PageHeader overline="Governance · Module 6 · InsightBridge 360" title="InsightBridge 360"
        description="Confidential feedback and organizational intelligence. Anonymous reports store no identity or metadata — transparent governance, not surveillance."
        actions={
          <div className="flex gap-2">
            <button data-testid="track-report-btn" onClick={() => setTrackOpen(true)}
              className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium hover:border-[#155EEF] hover:text-[#155EEF]"><Search size={15} /> Track</button>
            <button data-testid="submit-report-btn" onClick={() => setSubmitOpen(true)}
              className="flex items-center gap-2 rounded-md bg-[#7C3AED] px-4 py-2 text-sm font-semibold text-white hover:bg-[#6D28D9]"><Plus size={16} /> Submit Report</button>
          </div>} />

      {analytics && (
        <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-4">
          <StatCard icon={Megaphone} label="Total Reports" value={analytics.total_cases} accent="#7C3AED" index={0} onClick={() => setTab("Cases")} />
          <StatCard icon={AlertTriangle} label="Open Cases" value={analytics.open_cases} accent="#F79009" index={1} onClick={() => setTab("Cases")} />
          <StatCard icon={Clock} label="SLA Breaches" value={analytics.sla_breaches} accent="#F04438" index={2} />
          <StatCard icon={ShieldCheck} label="Culture Score" value={analytics.signals.culture_score} accent="#12B76A" index={3} onClick={() => setTab("Analytics")} />
        </div>
      )}

      <div className="mb-5 flex gap-1 rounded-lg border bg-card p-1 w-fit">
        {TABS.map((t) => (
          <button key={t} data-testid={`ib-tab-${t.toLowerCase()}`} onClick={() => setTab(t)}
            className={cn("rounded-md px-4 py-2 text-sm font-medium transition-colors", tab === t ? "bg-[#7C3AED] text-white" : "text-muted-foreground hover:bg-muted")}>{t}</button>
        ))}
      </div>

      {tab === "Cases" && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
          <div className="lg:col-span-5 rounded-xl border bg-card shadow-sm overflow-hidden">
            <div className="border-b px-4 py-3 text-sm font-bold">Case Work Queue</div>
            <div className="careiq-scroll max-h-[600px] divide-y overflow-y-auto">
              {cases.map((c) => {
                const A = ANON[c.anonymity]; const S = SENT[c.sentiment] || SENT.Neutral;
                return (
                  <button key={c.id} data-testid={`case-${c.id}`} onClick={() => openCase(c.id)}
                    className={cn("w-full px-4 py-3 text-left transition-colors hover:bg-muted/50", active?.id === c.id && "bg-muted/60")}>
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: A.c }}><A.icon size={12} /> {A.label}</span>
                      <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize" style={{ color: STATUS[c.status], backgroundColor: STATUS[c.status] + "1a" }}>{c.status.replace("-", " ")}</span>
                    </div>
                    <div className="mt-1 line-clamp-2 text-sm">{c.content}</div>
                    <div className="mt-1.5 flex items-center gap-2 text-[11px] text-muted-foreground">
                      <span className="rounded bg-muted px-1.5 py-0.5 font-medium">{c.category}</span>
                      <span className="flex items-center gap-1" style={{ color: S.c }}><S.icon size={11} /> {c.sentiment}</span>
                      {c.sla_breached && <span className="font-semibold text-[#F04438]">SLA breached</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-7 rounded-xl border bg-card shadow-sm flex flex-col overflow-hidden min-h-[400px]">
            {active ? (
              <>
                <div className="border-b px-4 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs font-semibold text-muted-foreground">{active.code}</span>
                      <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] font-medium">{active.category}</span>
                      <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold" style={{ color: scoreColor(100 - active.complaint_risk * 100), backgroundColor: scoreColor(100 - active.complaint_risk * 100) + "1a" }}>risk {Math.round(active.complaint_risk * 100)}%</span>
                    </div>
                    <span className="text-[11px] text-muted-foreground">SLA due {String(active.sla_due).slice(0, 16).replace("T", " ")}</span>
                  </div>
                  <p className="mt-2 text-sm">{active.content}</p>
                  <div className="mt-1 text-[11px] text-muted-foreground">
                    {active.anonymity === "anonymous" ? "Reporter identity is protected — no identity or metadata stored." : `Reporter: ${active.reporter_name}`}
                  </div>
                </div>
                <div className="careiq-scroll flex-1 space-y-3 overflow-y-auto p-4">
                  {(active.messages || []).map((m) => (
                    <div key={m.id} className={cn("flex", m.sender === "handler" ? "justify-end" : "justify-start")}>
                      <div className={cn("max-w-[80%] rounded-xl px-3.5 py-2.5 text-[13px]", m.sender === "handler" ? "bg-[#7C3AED] text-white" : "border bg-background")}>
                        <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide opacity-70">{m.sender_name}</div>{m.content}
                      </div>
                    </div>
                  ))}
                  {(active.messages || []).length === 0 && <div className="text-sm text-muted-foreground">No follow-up messages yet.</div>}
                </div>
                <div className="border-t p-3 space-y-2">
                  <div className="flex flex-wrap gap-1.5">
                    {["triage", "in-progress", "escalated", "resolved"].map((s) => (
                      <button key={s} data-testid={`case-status-${s}`} onClick={() => setStatus(s)}
                        className={cn("flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-medium capitalize transition-colors",
                          s === "escalated" ? "hover:border-[#F04438] hover:text-[#F04438]" : s === "resolved" ? "hover:border-[#12B76A] hover:text-[#12B76A]" : "hover:border-[#155EEF] hover:text-[#155EEF]")}>
                        {s === "escalated" ? <Siren size={12} /> : s === "resolved" ? <CheckCircle2 size={12} /> : null}{s.replace("-", " ")}</button>
                    ))}
                  </div>
                  <div className="flex items-end gap-2 rounded-lg border bg-background p-2 focus-within:border-[#7C3AED]">
                    <textarea data-testid="case-reply-input" rows={1} value={reply} onChange={(e) => setReply(e.target.value)}
                      placeholder="Secure follow-up to reporter…" className="max-h-24 flex-1 resize-none bg-transparent px-1 text-sm outline-none" />
                    <button data-testid="case-reply-btn" onClick={sendReply} disabled={!reply.trim()}
                      className="flex h-8 w-8 items-center justify-center rounded-md bg-[#7C3AED] text-white disabled:opacity-40"><Send size={14} /></button>
                  </div>
                </div>
              </>
            ) : <div className="flex flex-1 items-center justify-center text-muted-foreground"><Megaphone size={18} className="mr-2" /> Select a case</div>}
          </div>
        </div>
      )}

      {tab === "Analytics" && analytics && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <SignalCard label="Culture Score" value={analytics.signals.culture_score} suffix="/100" color={scoreColor(analytics.signals.culture_score)} />
            <SignalCard label="Burnout Risk" value={analytics.signals.burnout_risk} color={analytics.signals.burnout_risk === "High" ? "#F04438" : analytics.signals.burnout_risk === "Moderate" ? "#F79009" : "#12B76A"} />
            <SignalCard label="Retention Signal" value={analytics.signals.retention_signal} color={analytics.signals.retention_signal === "Watch" ? "#F79009" : "#12B76A"} />
            <SignalCard label="Complaint Trend" value={analytics.signals.complaint_trend} color={analytics.signals.complaint_trend === "Rising" ? "#F04438" : "#12B76A"} />
          </div>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
            <div className="lg:col-span-4 rounded-xl border bg-card p-5 shadow-sm">
              <div className="mb-3 text-sm font-bold">Sentiment</div>
              {["Positive", "Neutral", "Negative"].map((k) => {
                const S = SENT[k]; const total = analytics.total_cases || 1; const pct = Math.round(analytics.sentiment[k] / total * 100);
                return (
                  <div key={k} className="mb-3">
                    <div className="mb-1 flex justify-between text-xs"><span className="flex items-center gap-1.5" style={{ color: S.c }}><S.icon size={13} /> {k}</span><span className="font-semibold">{analytics.sentiment[k]} · {pct}%</span></div>
                    <div className="h-2 w-full rounded-full bg-muted"><div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: S.c }} /></div>
                  </div>
                );
              })}
            </div>
            <div className="lg:col-span-8 rounded-xl border bg-card p-5 shadow-sm">
              <div className="mb-3 text-sm font-bold">Reports by Category</div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={analytics.categories} margin={{ left: -18, right: 8, top: 8, bottom: 0 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} interval={0} angle={-12} textAnchor="end" height={50} />
                  <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))", fontSize: 12 }} />
                  <Bar dataKey="count" fill="#7C3AED" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {tab === "Surveys" && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {surveys.map((s) => (
            <div key={s.id} data-testid={`survey-${s.id}`} className="rounded-xl border bg-card p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <div><div className="text-sm font-bold">{s.title}</div><div className="text-xs text-muted-foreground">{s.type} · {s.audience} · {s.responses} responses</div></div>
                <div className="text-right"><div className="text-3xl font-bold" style={{ color: scoreColor(s.type === "NPS" || s.type === "eNPS" ? s.score + 50 : s.score) }}>{s.score}</div><div className="overline">score</div></div>
              </div>
              <ResponsiveContainer width="100%" height={70} className="mt-2">
                <LineChart data={(s.trend || []).map((v, i) => ({ i, v }))}>
                  <Line type="monotone" dataKey="v" stroke="#7C3AED" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
              {s.promoters != null && (
                <div className="mt-2 flex gap-3 text-xs">
                  <span className="text-[#12B76A]">▲ {s.promoters} promoters</span>
                  <span className="text-muted-foreground">● {s.passives} passives</span>
                  <span className="text-[#F04438]">▼ {s.detractors} detractors</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {submitOpen && <SubmitModal onClose={() => setSubmitOpen(false)} onDone={loadCases} />}
      {trackOpen && <TrackModal onClose={() => setTrackOpen(false)} />}
    </div>
  );
}

function SignalCard({ label, value, suffix = "", color }) {
  return (
    <div className="rounded-xl border bg-card p-4 shadow-sm">
      <div className="overline">{label}</div>
      <div className="mt-1 text-2xl font-bold" style={{ color }}>{value}{suffix}</div>
    </div>
  );
}

function SubmitModal({ onClose, onDone }) {
  const [form, setForm] = useState({ content: "", anonymity: "anonymous", channel: "Web", reporter_name: "" });
  const [code, setCode] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (form.content.trim().length < 5) { toast.error("Please add a bit more detail"); return; }
    setBusy(true);
    try { const { data } = await api.post("/insight/reports", form); setCode(data.tracking_code); onDone?.(); }
    catch (e) { toast.error("Submit failed"); } finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} title="Submit a Report">
      {code ? (
        <div className="text-center">
          <CheckCircle2 size={40} className="mx-auto text-[#12B76A]" />
          <p className="mt-3 text-sm">Report submitted securely.</p>
          <div className="mt-3 rounded-lg border bg-background p-3">
            <div className="overline">Your tracking code</div>
            <div data-testid="tracking-code" className="mt-1 font-mono text-lg font-bold text-[#7C3AED]">{code}</div>
            <p className="mt-1 text-xs text-muted-foreground">Save this to follow up anonymously — it's the only way to access your case.</p>
          </div>
          <button onClick={onClose} className="mt-4 rounded-md bg-[#7C3AED] px-4 py-2 text-sm font-semibold text-white">Done</button>
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Reporting Mode</label>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(ANON).map(([k, v]) => (
                <button key={k} data-testid={`anon-${k}`} onClick={() => setForm({ ...form, anonymity: k })}
                  className={cn("flex flex-col items-center gap-1 rounded-lg border p-2.5 text-xs font-medium transition-colors", form.anonymity === k ? "border-[#7C3AED] bg-[#7C3AED]/5 text-[#7C3AED]" : "hover:border-[#7C3AED]")}>
                  <v.icon size={16} /> {v.label}</button>
              ))}
            </div>
          </div>
          {form.anonymity !== "anonymous" && (
            <div><label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Your name {form.anonymity === "confidential" && "(kept confidential)"}</label>
              <input data-testid="reporter-name" value={form.reporter_name} onChange={(e) => setForm({ ...form, reporter_name: e.target.value })} className="ibinput" /></div>
          )}
          <div><label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Channel</label>
            <select data-testid="report-channel" value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} className="ibinput">{["Web", "Mobile", "QR Code", "Hotline", "SMS"].map((c) => <option key={c}>{c}</option>)}</select></div>
          <div><label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">What would you like to report?</label>
            <textarea data-testid="report-content" rows={4} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} placeholder="Describe the concern, feedback or recognition…" className="ibinput resize-none" /></div>
          <p className="text-[11px] text-muted-foreground">AI will triage sentiment, category and urgency. Anonymous mode stores no identity or metadata.</p>
          <button data-testid="report-submit-btn" onClick={submit} disabled={busy} className="w-full rounded-md bg-[#7C3AED] py-2.5 text-sm font-semibold text-white hover:bg-[#6D28D9] disabled:opacity-60">
            {busy ? "Submitting…" : "Submit Securely"}</button>
        </div>
      )}
      <style>{`.ibinput{width:100%;border-radius:0.5rem;border:1px solid hsl(var(--border));background:hsl(var(--background));padding:0.55rem 0.75rem;font-size:0.875rem;outline:none}.ibinput:focus{border-color:#7C3AED}`}</style>
    </Modal>
  );
}

function TrackModal({ onClose }) {
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [reply, setReply] = useState("");
  const track = async () => {
    try { const { data } = await api.get(`/insight/track/${code.trim().toUpperCase()}`); setResult(data); }
    catch (e) { toast.error("No report found for that code"); setResult(null); }
  };
  const send = async () => {
    if (!reply.trim()) return;
    try { await api.post("/insight/track/reply", { code: code.trim().toUpperCase(), content: reply }); toast.success("Reply sent"); setReply(""); track(); }
    catch (e) { toast.error("Failed"); }
  };
  return (
    <Modal onClose={onClose} title="Track a Report">
      <div className="flex gap-2">
        <input data-testid="track-code-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="IB-XXXXXXXX" className="ibinput font-mono" />
        <button data-testid="track-lookup-btn" onClick={track} className="rounded-md bg-[#7C3AED] px-4 text-sm font-semibold text-white">Look up</button>
      </div>
      {result && (
        <div className="mt-4 space-y-3">
          <div className="rounded-lg border bg-background p-3 text-sm">
            <div className="flex justify-between"><span className="font-mono text-xs text-muted-foreground">{result.case.code}</span>
              <span className="rounded-full px-2 py-0.5 text-xs font-semibold capitalize" style={{ color: STATUS[result.case.status], backgroundColor: STATUS[result.case.status] + "1a" }}>{result.case.status.replace("-", " ")}</span></div>
            <div className="mt-1 text-xs text-muted-foreground">{result.case.category} · {result.case.urgency} urgency</div>
          </div>
          <div className="space-y-2">
            {result.messages.map((m, i) => (
              <div key={i} className={cn("rounded-lg px-3 py-2 text-[13px]", m.sender === "reporter" ? "bg-[#155EEF] text-white ml-8" : "border bg-background mr-8")}>
                <div className="mb-0.5 text-[10px] font-semibold uppercase opacity-70">{m.sender_name}</div>{m.content}</div>
            ))}
            {result.messages.length === 0 && <div className="text-xs text-muted-foreground">No messages yet. A handler will respond soon.</div>}
          </div>
          <div className="flex gap-2">
            <input data-testid="track-reply-input" value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Add a message…" className="ibinput" />
            <button data-testid="track-reply-btn" onClick={send} className="rounded-md bg-[#155EEF] px-4 text-sm font-semibold text-white">Send</button>
          </div>
        </div>
      )}
      <style>{`.ibinput{width:100%;border-radius:0.5rem;border:1px solid hsl(var(--border));background:hsl(var(--background));padding:0.55rem 0.75rem;font-size:0.875rem;outline:none}.ibinput:focus{border-color:#7C3AED}`}</style>
    </Modal>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="careiq-scroll relative max-h-[90vh] w-full max-w-md overflow-y-auto rounded-xl border bg-card p-6 shadow-2xl">
        <div className="mb-4 flex items-center justify-between"><h3 className="text-lg font-bold">{title}</h3><button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={20} /></button></div>
        {children}
      </div>
    </div>
  );
}
